export const getFromPriceYear = (schedules = []) => {
  const years = (Array.isArray(schedules) ? schedules : [])
    .map((s) => (s?.start_date ? new Date(s.start_date).getFullYear() : null))
    .filter((y) => Number.isFinite(y));
  if (years.length === 0) return new Date().getFullYear();
  const currentYear = new Date().getFullYear();
  const upcoming = years.filter((y) => y >= currentYear).sort((a, b) => a - b);
  return upcoming[0] ?? years.sort((a, b) => a - b)[0];
};

export const getPickupRegionId = (pickup) => (
  Number(
    pickup?.region_id
    ?? pickup?.region?.id
    ?? pickup?.accommodation?.area?.id
    ?? pickup?.accommodation?.region_id
    ?? pickup?.acc?.area?.id
    ?? pickup?.acc?.region_id
    ?? 0
  ) || 0
);

export const buildMinimumAdultsByScheduleId = (schedules = []) => {
  const map = new Map();
  (Array.isArray(schedules) ? schedules : []).forEach((s) => {
    const sid = Number(s?.id || 0);
    if (!sid) return;
    map.set(String(sid), Math.max(1, Number(s?.minimum_adults ?? 1) || 1));
  });
  return map;
};

export const extractLowestFromPricelistRows = (rows, minimumAdultsByScheduleId) => {
  if (!Array.isArray(rows)) return null;
  let best = null;
  rows.forEach((row) => {
    const unitPrice = Number(row?.final_price ?? row?.price);
    if (!Number.isFinite(unitPrice) || unitPrice < 0) return;
    const scheduleId = Number(row?.schedule_id || 0);
    const minimumAdults = Math.max(1, Number(minimumAdultsByScheduleId.get(String(scheduleId)) ?? 1) || 1);
    const displayPrice = unitPrice * minimumAdults;
    if (!best || displayPrice < best.price) {
      best = { price: displayPrice, minimumAdults, scheduleId };
    }
  });
  return best;
};

export const resolveExcursionPickupRows = (excursion, fallbackPickups = []) => {
  const ownPickups = Array.isArray(excursion?.pickups) ? excursion.pickups : [];
  if (ownPickups.length > 0) return ownPickups;
  const excId = Number(excursion?.id || 0);
  if (!excId || !Array.isArray(fallbackPickups)) return [];
  return fallbackPickups.filter((p) => Number(p?.exc_id ?? p?.excursion_id ?? 0) === excId);
};

export const getPickupRegionIdsForExcursion = (excursion, fallbackPickups = []) => {
  const pickupRows = resolveExcursionPickupRows(excursion, fallbackPickups);
  return Array.from(new Set(pickupRows.map((p) => getPickupRegionId(p)).filter((id) => id > 0)));
};

export const fetchLowestFromPriceForExcursion = async ({
  excursion,
  getPricelists,
  fallbackPickups = [],
}) => {
  if (!excursion?.id || typeof getPricelists !== "function") return null;
  const year = getFromPriceYear(excursion?.schedules ?? []);
  const minimumAdultsByScheduleId = buildMinimumAdultsByScheduleId(excursion?.schedules ?? []);
  const regionIds = getPickupRegionIdsForExcursion(excursion, fallbackPickups);
  const requests = [
    getPricelists(year, excursion.id),
    ...regionIds.map((regionId) => getPricelists(year, excursion.id, regionId)),
  ];
  const results = await Promise.allSettled(requests);
  const mins = results
    .filter((r) => r.status === "fulfilled")
    .map((r) => extractLowestFromPricelistRows(r.value?.data?.data ?? r.value?.data, minimumAdultsByScheduleId))
    .filter(Boolean);
  if (!mins.length) return null;
  return mins.reduce((acc, cur) => (cur.price < acc.price ? cur : acc), mins[0]);
};
